// Steht eine Ergänzung schon im Vertrag? (seit 29.09.2026, Wunsch des Nutzers)
//
// Anlass: Beim Bearbeiten hochgeladener Verträge schlug das Gespräch Passus
// vor, die im Vertrag schon standen - wörtlich oder fast. Ob etwas doppelt
// ist, hing allein daran, ob die KI es bemerkte und in "beruehrt" eintrug.
// Hier wird es ohne KI geprüft, an dem Vertrag, wie er gerade ist: Jeder
// Absatz einer Ergänzung wird mit jedem Absatz des Vertrags verglichen.
//
// Verglichen werden Dreiergruppen aufeinanderfolgender Wörter. Einzelne
// gemeinsame Wörter ("Nutzer", "Grundstück") sagen nichts; drei in Folge
// schon. Der Anteil ist, wie viele Dreiergruppen des Ergänzungsabsatzes in
// einem Vertragsabsatz (oder zwei aufeinanderfolgenden - lange Absätze
// können geteilt sein) vorkommen.
//
// Was eine Ergänzung selbst in den Vertrag gebracht hat, zählt nicht: Beim
// Einfügen bekommt jedes ihrer Elemente data-optional-clause-id
// ="verhandlung-<id>" (baueKlauselHtml in api/_lib/verhandlungsStore.js).

// Ab diesem Anteil gilt ein Absatz als schon vorhanden. Eine Ergänzung, die
// eine bestehende Klausel nur in einer Zahl ändert ("drei" statt "sechs
// Monate"), liegt bei kurzen Sätzen deutlich darunter; eine, die eine lange
// Klausel mit kleiner Änderung wiederholt, darüber - dann ist sie auch
// besser eine Ersetzung.
export const DOPPELT_AB = 0.6;
// Kürzer sagt ein Absatz zu wenig - "Die Kosten trägt der Nutzer." stünde
// sonst in jedem Vertrag "doppelt".
const MINDEST_DREIER = 4;

const ENTITAETEN = {
  auml: 'ä', ouml: 'ö', uuml: 'ü', Auml: 'Ä', Ouml: 'Ö', Uuml: 'Ü', szlig: 'ß',
  nbsp: ' ', sect: '§', amp: '&', quot: '"', apos: "'", euro: '€',
  ndash: '–', mdash: '—', bdquo: '„', ldquo: '“', rdquo: '”',
};

const QUELLE = /<article\b[^>]*\bid=(["'])contract-source\1[^>]*>([\s\S]*?)<\/article>/i;
const BLOCK = /<(p|h[1-6]|li)\b([^>]*)>([\s\S]*?)<\/\1\s*>/gi;
const KLAUSEL_ID = /\bdata-optional-clause-id=(["'])(.*?)\1/i;
const NUMMER = /\bclass=(["'])[^"']*\bclause-number\b[^"']*\1[^>]*>([\s\S]*?)<\/span>/i;

function alsText(html) {
  return String(html || '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&#x([0-9a-f]+);/gi, (_m, h) => String.fromCodePoint(parseInt(h, 16)))
    .replace(/&#(\d+);/g, (_m, n) => String.fromCodePoint(Number(n)))
    .replace(/&([A-Za-z]+);/g, (_m, name) => ENTITAETEN[name] ?? ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function woerter(text) {
  return String(text || '').toLowerCase().split(/[^\p{L}\p{N}]+/u).filter(Boolean);
}

function dreiergruppen(text) {
  const w = woerter(text);
  const gruppen = new Set();
  for (let i = 0; i + 2 < w.length; i += 1) gruppen.add(`${w[i]} ${w[i + 1]} ${w[i + 2]}`);
  return gruppen;
}

function stelleFuer(paragraph, nummer, text) {
  const nr = alsText(nummer).replace(/\.$/, '');
  if (paragraph) return nr ? `${paragraph}, Nr. ${nr}` : paragraph;
  return text.length > 60 ? `„${text.slice(0, 60)}…“` : `„${text}“`;
}

/**
 * Die Absätze des Vertrags - aus <article id="contract-source">, wenn es das
 * gibt (sonst stünde jeder Absatz der gerenderten Seiten noch einmal da), mit
 * einer lesbaren Stelle ("§ 2 Eigentum und Nutzungsrechte, Nr. 2").
 */
export function vertragsBloecke(html) {
  const roh = String(html || '');
  const quelle = roh.match(QUELLE);
  const koerper = quelle ? quelle[2] : roh;
  const bloecke = [];
  let paragraph = '';
  // Fuer die Stelle einer Ersetzung (ortImVertrag, seit 24.10.2026): die letzte
  // Ueberschrift - auch „Präambel“ und Anlagen - und die Nummer des Absatzes,
  // zu dem ein Block gehoert. Spiegelstriche unter „5.“ gehoeren zu Abs. 5.
  let abschnitt = '';
  let absatz = '';
  for (const [, tag, attribute, innen] of koerper.matchAll(BLOCK)) {
    const text = alsText(innen);
    if (!text) continue;
    if (/^h[1-3]$/i.test(tag)) {
      abschnitt = text.slice(0, 80);
      absatz = '';
    }
    if (/^h[1-3]$/i.test(tag) && /^§\s*\d+/.test(text)) paragraph = text.slice(0, 80);
    const nummer = (innen.match(NUMMER) || [])[2];
    if (nummer) absatz = alsText(nummer).replace(/\.$/, '');
    bloecke.push({
      text,
      stelle: stelleFuer(paragraph, nummer, text),
      abschnitt,
      absatz: /^h[1-6]$/i.test(tag) ? '' : absatz,
      folge: ` ${woerter(text).join(' ')} `,
      klausel: (attribute.match(KLAUSEL_ID) || [])[2] || '',
      gruppen: dreiergruppen(text),
    });
  }
  // Mit dem nächsten zusammen gelesen - für Absätze, die an einer
  // Seitengrenze geteilt sind. Die Wortgruppen über die Trennstelle hinweg
  // gäbe es sonst in keinem der beiden.
  bloecke.forEach((block, i) => {
    const naechster = bloecke[i + 1];
    block.mitNaechstem = naechster ? dreiergruppen(`${block.text} ${naechster.text}`) : null;
  });
  return bloecke;
}

function anteilIn(gesucht, bereich) {
  if (!gesucht.size || !bereich) return 0;
  let gefunden = 0;
  gesucht.forEach((g) => { if (bereich.has(g)) gefunden += 1; });
  return gefunden / gesucht.size;
}

/**
 * Steht diese Ergänzung schon im Vertrag? Der Absatz mit der größten
 * Übereinstimmung - oder null, wenn keiner über DOPPELT_AB liegt.
 *
 * @returns {{ stelle: string, anteil: number, absatz: number, auszug: string } | null}
 *   anteil in Prozent, absatz ab 1
 */
export function schonImVertrag(passus, bloecke, { ab = DOPPELT_AB } = {}) {
  const eigene = passus?.id ? `verhandlung-${passus.id}` : '';
  const alle = bloecke || [];
  const zaehlt = (block) => Boolean(block) && (!eigene || block.klausel !== eigene);
  let bester = null;
  const merke = (anteil, block, i) => {
    // Nur ein echtes Mehr gewinnt: Bei Gleichstand bleibt der zuerst
    // gefundene einzelne Absatz - sonst nennte ein Paar "Nr. 1 + Nr. 2" die
    // falsche Nummer.
    if (!bester || anteil > bester.anteil) {
      bester = { anteil, stelle: block.stelle, absatz: i + 1, auszug: block.text.slice(0, 200) };
    }
  };
  (passus?.absaetze || []).forEach((absatz, i) => {
    const gruppen = dreiergruppen(absatz?.text);
    if (gruppen.size < MINDEST_DREIER) return;
    const einzeln = alle.map((block) => (zaehlt(block) ? anteilIn(gruppen, block.gruppen) : 0));
    alle.forEach((block, j) => { if (zaehlt(block)) merke(einzeln[j], block, i); });
    // Paare erst danach: Sie zählen nur, wenn sie MEHR abdecken als jeder
    // der beiden allein - gemeldet wird der Absatz mit dem größeren Anteil.
    alle.forEach((block, j) => {
      const naechster = alle[j + 1];
      if (!zaehlt(block) || !zaehlt(naechster)) return;
      const paar = anteilIn(gruppen, block.mitNaechstem);
      if (paar > Math.max(einzeln[j], einzeln[j + 1])) {
        merke(paar, einzeln[j] >= einzeln[j + 1] ? block : naechster, i);
      }
    });
  });
  if (!bester || bester.anteil < ab) return null;
  return { ...bester, anteil: Math.round(bester.anteil * 100) };
}

// Nur hier ist ein vorgegebener Wortlaut etwas, das hinzukaeme. Beim Streichen
// ist das Zitat der Text, der weg soll - der steht naturgemaess im Vertrag.
const ARTEN_MIT_NEUEM_TEXT = ['ergaenzen', 'aendern'];

/**
 * Für einen Punkt der Gliederung (seit 29.09.2026): Steht der vom Schreiben
 * vorgegebene Wortlaut schon im Vertrag (ohne KI), oder hat die KI beim
 * Gliedern gesagt, der Vertrag regle das schon ("schonGeregelt")? Die meisten
 * Punkte haben nur eine Forderung in eigenen Worten - dafür taugt der
 * Wortvergleich nicht, dafür gibt es die Angabe der KI.
 *
 * @returns {{ treffer: object | null, laut: string } | null}
 */
export function punktImVertrag(punkt, bloecke) {
  const absaetze = ARTEN_MIT_NEUEM_TEXT.includes(punkt?.art)
    ? String(punkt?.zitat || '').split(/\n+/).map((t) => t.trim()).filter(Boolean).map((text) => ({ text }))
    : [];
  const treffer = absaetze.length ? schonImVertrag({ absaetze }, bloecke) : null;
  const laut = String(punkt?.schonGeregelt || '').trim();
  if (!treffer && !laut) return null;
  return { treffer, laut };
}

/** "Wortlaut: § 2 …, Nr. 2 — 94 % gleich · laut KI: § 10 Abs. 1" */
export function punktImVertragText(befund) {
  if (!befund) return '';
  const teile = [];
  if (befund.treffer) teile.push(`Wortlaut: ${imVertragText(befund.treffer)}`);
  if (befund.laut) teile.push(`laut KI: ${befund.laut}`);
  return teile.join(' · ');
}

/** "Absatz 2: § 2 …, Nr. 2 — 94 % gleich" - die Nummer des Absatzes nur bei mehreren. */
export function imVertragText(treffer, absaetzeGesamt = 1) {
  if (!treffer) return '';
  const welcher = absaetzeGesamt > 1 ? `Absatz ${treffer.absatz}: ` : '';
  return `${welcher}${treffer.stelle} — ${treffer.anteil} % gleich`;
}

// ---------- Wo im Vertrag? (seit 24.10.2026, Wunsch des Nutzers) ----------
//
// „Dass mir immer angezeigt wird, wo es geändert oder ersetzt wird – welcher
// Paragraph.“ Ohne KI: Gesucht wird der Anfang des Wortlauts (acht Wörter, ohne
// Groß/klein und Satzzeichen) in den Blöcken des Vertrags; die Stelle ist die
// Überschrift darüber und die Nummer des Absatzes, zu dem der Block gehört.

const ANFANG_WOERTER = 8;

/**
 * Die Stelle eines Wortlauts im Vertrag: { abschnitt, absatz, mehrdeutig } oder
 * null. Steht der Anfang in mehreren Blöcken, entscheidet der ganze Wortlaut;
 * bleibt es mehrdeutig, gilt der erste Treffer mit `mehrdeutig: true`.
 */
export function ortImVertrag(text, bloecke) {
  const w = woerter(text);
  if (w.length < 3 || !Array.isArray(bloecke) || !bloecke.length) return null;
  // Zitiert die KI die Nummer mit („5. Folgende …“), steht sie im Block in
  // einem eigenen Element - mit und ohne versuchen.
  const versuche = /^\d/.test(w[0]) && w.length > 3 ? [w, w.slice(1)] : [w];
  for (const liste of versuche) {
    const anfang = ` ${liste.slice(0, ANFANG_WOERTER).join(' ')} `;
    let treffer = bloecke.filter((b) => (b.folge || '').includes(anfang));
    if (treffer.length > 1) {
      const ganz = ` ${liste.join(' ')} `;
      const genauer = treffer.filter((b) => b.folge.includes(ganz));
      if (genauer.length) treffer = genauer;
    }
    if (treffer.length) {
      return { abschnitt: treffer[0].abschnitt || '', absatz: treffer[0].absatz || '', mehrdeutig: treffer.length > 1 };
    }
  }
  return null;
}

/**
 * Wo eine Ersetzung greift: am alten Wortlaut, sonst (schon eingesetzt) am
 * neuen. { abschnitt, absatz, mehrdeutig, gefunden: 'alt' | 'neu' } oder null.
 */
export function ersetzungsOrt(aenderung, bloecke) {
  const alt = ortImVertrag(aenderung?.fundstelle, bloecke);
  if (alt) return { ...alt, gefunden: 'alt' };
  if (aenderung?.art === 'streichen') return null;
  const neu = ortImVertrag(aenderung?.neu, bloecke);
  return neu ? { ...neu, gefunden: 'neu' } : null;
}

/** "§ 5 Pachtzahlung und Beteiligung · Abs. 5" */
export function ortText(ort) {
  if (!ort) return '';
  const abschnitt = ort.abschnitt || 'Vertragsbeginn';
  return ort.absatz ? `${abschnitt} · Abs. ${ort.absatz}` : abschnitt;
}
